import { Box } from '@mui/material';
import * as echarts from 'echarts';
import React, { useCallback, useEffect } from 'react';
import { TrainingStatusChartLabelTypes } from '../../../../state/ducks/dashboard.new/training.stats/types';
import Text from '../../../components/Text';
import Colors from '../../../layout/theme/utils/colors';
import { linearScale } from '../../line.items/common/Utils';
import DoughnutChartOptions from './DoughnutChartOptions';
import { ChartContainer, ChartScroll, Dot, StyledTab, StyledTabs, TabLabel, TabWrapper, Title } from './TrainingChart.styles';

interface TrainingChartPresenterProps {
  overdue: number
  dueInSeven: number
  tabValue: number
  infoChangeTab: (newValue: number) => void
}

const TrainingChartPresenter: React.FC<TrainingChartPresenterProps> = ({
  overdue,
  dueInSeven,
  tabValue,
  infoChangeTab,
}) => {
  const totalCount = overdue + dueInSeven;
  const scaleNumber = linearScale([0, totalCount], [1, 50]);
  const getScaledValue = (value: number) => {
    if (value === 0) { return 0; }
    return Math.floor(scaleNumber(value));
  };
  const overDueScaledCount = getScaledValue(overdue);
  const totalScaledCount = getScaledValue(totalCount);
  const dueInSevenScaledCount = getScaledValue(dueInSeven);

  const data = [
    {
      value: overDueScaledCount === totalScaledCount ? overDueScaledCount - dueInSevenScaledCount : overDueScaledCount,
      name: TrainingStatusChartLabelTypes.OVERDUE,
      itemStyle: { color: Colors.latest_red_varient },
      detail: { show: false },
      title: { show: false },
    },
    {
      value: dueInSeven === 0 ? dueInSevenScaledCount : totalScaledCount,
      name: TrainingStatusChartLabelTypes.DUE,
      itemStyle: { color: Colors.latest_yellow_varient },
      detail: { show: false },
      title: { show: false },
    },
  ];

  const defaultTitle = {
    text: `${totalCount}`,
    subtext: TrainingStatusChartLabelTypes.TOTAL_OUTSTANDING,
    textStyle: { fontWeight: 'bold', color: Colors.almost_black, fontSize: 40 },
    subtextStyle: { fontWeight: 'bold', color: Colors.font_gray, fontSize: 12, lineHeight: 50 },
    itemGap: -20,
    left: 'center',
    top: '55%',
  };

  DoughnutChartOptions.title = defaultTitle;
  DoughnutChartOptions.series.data = data;
  DoughnutChartOptions.series.max = totalScaledCount + 0.001;

  let myChart: echarts.ECharts | undefined;

  const updateChart = useCallback(
    (tabValue: number) => {
      let text = '';
      let subText = '';
      let color = '';

      if (tabValue === 0) {
        text = `${totalCount}`;
        subText = TrainingStatusChartLabelTypes.TOTAL_OUTSTANDING;
        color = Colors.gray;
        DoughnutChartOptions.series.data[0].itemStyle.color = Colors.latest_red_varient;
        DoughnutChartOptions.series.data[1].itemStyle.color = Colors.latest_yellow_varient;
      } else if (tabValue === 2) {
        text = `${dueInSeven}`;
        subText = TrainingStatusChartLabelTypes.DUE;
        color = Colors.latest_yellow_varient;
        DoughnutChartOptions.series.data[0].itemStyle.color = Colors.platinum_grey;
        DoughnutChartOptions.series.data[1].itemStyle.color = Colors.latest_yellow_varient;
      } else if (tabValue === 1) {
        text = `${overdue}`;
        subText = TrainingStatusChartLabelTypes.OVERDUE;
        color = Colors.latest_red_varient;
        DoughnutChartOptions.series.data[0].itemStyle.color = Colors.latest_red_varient;
        DoughnutChartOptions.series.data[1].itemStyle.color = Colors.platinum_grey;
      }

      DoughnutChartOptions.title.text = text;
      DoughnutChartOptions.title.subtext = subText;
      DoughnutChartOptions.title.subtextStyle.color = color;
      DoughnutChartOptions.series.max = getScaledValue(totalCount) + 0.001;
      myChart?.setOption(DoughnutChartOptions);
    },
    [tabValue, overdue, dueInSeven, totalCount],
  );

  useEffect(() => {
    myChart = echarts.init(document.getElementById('training-chart') as HTMLDivElement);
    myChart.setOption(DoughnutChartOptions);

    const handleResize = () => myChart?.resize();
    window.addEventListener('resize', handleResize);

    myChart.on('mouseover', (evt: any) => {
      DoughnutChartOptions.series.data.forEach((data, index) => {
        if (index !== evt?.dataIndex) data.itemStyle.color = Colors.platinum_grey;
        if (index === evt?.dataIndex && index === 0) { DoughnutChartOptions.series.data[index].itemStyle.color = Colors.latest_red_varient; }
        if (index === evt?.dataIndex && index === 1) { DoughnutChartOptions.series.data[index].itemStyle.color = Colors.latest_yellow_varient; }
      });

      DoughnutChartOptions.title.subtext = evt?.data?.name;
      if (evt.data?.name === TrainingStatusChartLabelTypes.OVERDUE) {
        DoughnutChartOptions.title.subtextStyle.color = Colors.latest_red_varient;
        DoughnutChartOptions.title.text = overdue.toString();
      } else if (evt.data?.name === TrainingStatusChartLabelTypes.DUE) {
        DoughnutChartOptions.title.subtextStyle.color = Colors.latest_yellow_varient;
        DoughnutChartOptions.title.text = dueInSeven.toString();
      }

      myChart?.setOption(DoughnutChartOptions);
    });

    myChart.on('mouseout', () => updateChart(tabValue));

    updateChart(tabValue);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart?.off('mouseover');
      myChart?.off('mouseout');
    };
  }, [tabValue, dueInSeven, overdue, totalCount, updateChart]);

  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    infoChangeTab(newValue);
  };
  const tabCustomClasses = (index) => {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`,
    };
  };

  return (
    <ChartContainer>
      <Box display="flex" justifyContent="center">
        <Title color="textSecondary" gutterBottom>
          <Text translation="training.stats.title" />
        </Title>
      </Box>
      <Box display="flex" justifyContent="center">
        <ChartScroll id="training-chart" />
      </Box>
      <Box display="flex" justifyContent="center" mt={2}>
        <StyledTabs value={value} onChange={handleChange}>
          <StyledTab
            label={
              <TabLabel inactive={value !== 0}>
                <Text translation="training.stats.all" />
              </TabLabel>
            }
            {...tabCustomClasses(0)}
          />
          <StyledTab
            label={
              <TabWrapper>
                <Dot color={Colors.latest_red_varient} />
                <TabLabel inactive={value !== 1}>
                  <Text translation="common.overdue" />
                </TabLabel>
              </TabWrapper>
            }
            {...tabCustomClasses(2)}
          />
          <StyledTab
            label={
              <TabWrapper>
                <Dot color={Colors.latest_yellow_varient} />
                <TabLabel inactive={value !== 2}>
                  <Text translation="common.duein7days" />
                </TabLabel>
              </TabWrapper>
            }
            {...tabCustomClasses(1)}
          />
        </StyledTabs>
      </Box>
    </ChartContainer>
  );
};

export default React.memo(TrainingChartPresenter);
